/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable eqeqeq */
import React, { useRef, useState, useEffect, useCallback } from "react";
import { ContactList } from "../lib/OptionsAPI";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Button } from "./ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "./ui/command";
import { CheckIcon, XIcon } from "lucide-react";
import { cn } from "../lib/utils";
import { CaretSortIcon } from "@radix-ui/react-icons";
import { debounce } from "lodash";
import AddEditContact from "../pages/Contacts/AddEditContact";
import { ResponsiveDrawerForm } from "./ResponsiveDrawerForm";
import { getModuleNamingBySlug } from "../lib/Misc";
import { useOrganization } from "../lib/context/OrganisationContext";

const ContactSelect = ({
  trustId,
  frameworkData,
  optionType,
  handleSelect,
  handleBlur,
  className,
  disabled = false,
  initialValue,
  setContactList,
  setContactLoading,
  onClear,
  amlctfContact = false,
  loading = false,
  clearable = true,
}) => {
  const { organizationData } = useOrganization();
  const frameworks = frameworkData.filter(
    (item, index, self) =>
      index === self.findIndex((t) => t.value === item.value),
  );
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState(initialValue);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [searchTerm, setSearchTerm] = useState(""); // Store search input
  const scrollRef = useRef(null); // Ref for tracking scrolling

  const [showAddContact, setShowAddContact] = useState(false);

  const toggleShowAddContact = () => {
    if (showAddContact) {
      fetchContacts();
    }
    setShowAddContact(!showAddContact);
  };

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  // Function to load more suburbs

  const isLoadingMoreRef = useRef(false); // Prevent concurrent calls

  const loadMore = async () => {
    if (!hasMore || isLoadingMoreRef.current) return;

    isLoadingMoreRef.current = true;
    const nextPage = page + 1;

    const moreData = await ContactList(
      organizationData,
      trustId,
      page + 1,
      setContactLoading,
      setContactList,
      true,
      amlctfContact,
    );
    setPage(nextPage);
    setHasMore(moreData);

    isLoadingMoreRef.current = false;
  };

  // Manual scroll detection
  const handleScroll = useCallback(
    debounce(() => {
      if (!scrollRef.current) return;

      const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;

      if (scrollTop + clientHeight >= scrollHeight - 10) {
        loadMore();
      }
    }, 200), // 200ms debounce
    [page, hasMore],
  );

  const fetchContacts = useCallback(
    debounce(async (query) => {
      setPage(1); // Reset pagination on new search
      setHasMore(true);
      const moreData = await ContactList(
        organizationData,
        trustId,
        1, // Start from page 1 on new search
        setContactLoading,
        setContactList,
        true, // Replace list on new search
        amlctfContact,
        query, // Pass the search query
      );
      setHasMore(moreData);
    }, 500), // 500ms delay
    [],
  );

  useEffect(() => {
    if (trustId) {
      frameworks?.length == 0 && fetchContacts();
    }
  }, [trustId]);

  const handleSearch = (e) => {
    const query = e;
    setSearchTerm(query);
    fetchContacts(query);
  };

  const handleClear = (e) => {
    setValue("");
    onClear && onClear("");
  };

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className={`w-full justify-between h-9 px-3 ${
              value
                ? "font-normal text-left overflow-hidden"
                : "text-muted-foreground font-normal"
            } ${className || ""}`}
            onBlur={handleBlur}
            disabled={disabled}
            title={
              value
                ? frameworks.find((framework) => framework?.value == value)
                    ?.label
                : ""
            }
          >
            <span className="truncate flex-1 min-w-0 text-left">
              {value
                ? frameworks.find((framework) => framework?.value == value)
                    ?.label
                : `Select ${optionType}...`}
            </span>
            {clearable && value && !loading ? (
              <XIcon
                className="ml-2 h-4 w-4 shrink-0 cursor-pointer opacity-50"
                onClick={(event) => {
                  event.stopPropagation();
                  handleClear();
                }}
              />
            ) : (
              <CaretSortIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-full p-0"
          modal={false}
          onPointerDownOutside={(e) => e.stopPropagation()}
          onFocusOutside={(e) => e.preventDefault()}
          onWheelCapture={(e) => e.stopPropagation()}
        >
          <Command>
            <CommandInput
              placeholder={`Search ${optionType}...`}
              className="h-9"
              value={searchTerm}
              onValueChange={handleSearch}
              disablePointerSelection={!trustId}
            />
            <CommandList
              ref={scrollRef}
              onScroll={handleScroll} // Attach scroll event listener
              className="max-h-60 overflow-y-auto"
            >
              <CommandEmpty>No {optionType} found.</CommandEmpty>
              <CommandGroup>
                {frameworks.map((framework) => (
                  <CommandItem
                    key={framework?.value}
                    value={framework?.label}
                    onSelect={(currentValue) => {
                      setOpen(false);
                      if (
                        currentValue ===
                        `Add ${getModuleNamingBySlug(
                          organizationData?.organizationData,
                          "contacts",
                          "Customer",
                        )}`
                      ) {
                        setShowAddContact(true);
                      } else {
                        handleSelect(
                          frameworks.find(
                            (framework) => framework?.label === currentValue,
                          )?.value,
                        );
                        setValue(
                          frameworks.find(
                            (framework) => framework?.label === currentValue,
                          )?.value,
                        );
                      }
                    }}
                    disabled={disabled}
                    className={value === framework?.value ? "optionActive" : ""}
                  >
                    {framework?.label}
                    <CheckIcon
                      className={cn(
                        "ml-auto h-4 w-4 optionChecked",
                        value === framework?.value
                          ? "opacity-100"
                          : "opacity-0",
                      )}
                    />
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      <ResponsiveDrawerForm
        description={""}
        title={`Add ${getModuleNamingBySlug(
          organizationData?.organizationData,
          "contacts",
          "Customer",
        )}`}
        handleOpen={() => {}}
        handleClose={() => {
          setShowAddContact(false);
        }}
        buttonText={""}
        className={""}
        open={showAddContact}
        buttonClassName={""}
        setOpen={setShowAddContact}
        write={true}
      >
        <AddEditContact
          edit={false}
          toggleShowAddEditContact={toggleShowAddContact}
          trustId={trustId}
        />
      </ResponsiveDrawerForm>
    </>
  );
};

export default ContactSelect;
